import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { API } from '../../../contexts/AuthContext';
import { confirmDialog, errorAlert } from '../../../helpers/alerts';
import { LAYOUTS } from '../../../helpers/camerasLayout';

// Tours personalizados ("so os caixas", "depositos"...): cameras de qualquer
// equipamento, na ordem escolhida, com layout e tempo de troca proprios.

const INTERVALOS = [10, 15, 20, 30, 45, 60, 120];
const FORM_VAZIO = { id: null, nome: '', layout: '9', intervalo: 20, ativo: true, ordem: 0, camera_ids: [] };

export default function ToursAba({ onAlterou }) {
  const [tours, setTours] = useState([]);
  const [cameras, setCameras] = useState([]);
  const [grupos, setGrupos] = useState([]);
  const [gravadores, setGravadores] = useState([]);
  const [form, setForm] = useState(null);
  const [filtro, setFiltro] = useState({ busca: '', equip: '', grupo: '' });
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');

  const carregar = useCallback(async () => {
    try {
      const [t, c, g] = await Promise.all([
        axios.get(`${API}/cameras/tours`),
        axios.get(`${API}/cameras/cameras`),
        axios.get(`${API}/cameras/gravadores`),
      ]);
      setTours(t.data?.data?.tours || []);
      setCameras(c.data?.data?.cameras || []);
      setGrupos(c.data?.data?.grupos || []);
      setGravadores(g.data?.data?.gravadores || []);
      setErro('');
    } catch (e) {
      setErro(e.response?.data?.message || 'Não foi possível carregar os tours.');
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const gravadoresPorId = useMemo(() => new Map(gravadores.map((g) => [g.id, g])), [gravadores]);
  const camerasPorId = useMemo(() => new Map(cameras.map((c) => [c.id, c])), [cameras]);
  const rotulo = (c) => `${gravadoresPorId.get(c.gravador_id)?.nome || '?'} · ${c.nome_exibicao}`;

  const disponiveis = useMemo(() => {
    if (!form) return [];
    const noTour = new Set(form.camera_ids);
    const termo = filtro.busca.trim().toLowerCase();
    return cameras
      .filter((c) => !noTour.has(c.id))
      .filter((c) => !filtro.equip || String(c.gravador_id) === filtro.equip)
      .filter((c) => !filtro.grupo || String(c.grupo_id) === filtro.grupo)
      .filter((c) => !termo || rotulo(c).toLowerCase().includes(termo))
      .sort((a, b) => rotulo(a).localeCompare(rotulo(b), 'pt-BR', { numeric: true }));
  }, [form, cameras, filtro, gravadoresPorId]); // eslint-disable-line react-hooks/exhaustive-deps

  const setCampo = (campo, v) => setForm((f) => ({ ...f, [campo]: v }));
  const adicionar = (ids) => setForm((f) => ({ ...f, camera_ids: [...f.camera_ids, ...ids] }));
  const remover = (id) => setForm((f) => ({ ...f, camera_ids: f.camera_ids.filter((x) => x !== id) }));
  const mover = (i, passo) => setForm((f) => {
    const ids = [...f.camera_ids];
    const j = i + passo;
    if (j < 0 || j >= ids.length) return f;
    [ids[i], ids[j]] = [ids[j], ids[i]];
    return { ...f, camera_ids: ids };
  });

  const salvar = async () => {
    setSalvando(true);
    setErro('');
    try {
      const corpo = { ...form, intervalo: Number(form.intervalo), ordem: Number(form.ordem) || 0 };
      if (form.id) await axios.put(`${API}/cameras/tours/${form.id}`, corpo);
      else await axios.post(`${API}/cameras/tours`, corpo);
      setForm(null);
      await carregar();
      onAlterou();
    } catch (e) {
      setErro(e.response?.data?.message || 'Não foi possível salvar o tour.');
    } finally {
      setSalvando(false);
    }
  };

  const excluir = async (t) => {
    const r = await confirmDialog({
      title: `Excluir o tour "${t.nome}"?`,
      text: 'As câmeras não são afetadas.',
      confirmButtonText: 'Excluir',
    });
    if (!r.isConfirmed) return;
    try {
      await axios.delete(`${API}/cameras/tours/${t.id}`);
      await carregar();
      onAlterou();
    } catch (e) {
      errorAlert({ title: 'Erro', text: e.response?.data?.message || 'Não foi possível excluir.' });
    }
  };

  if (form) {
    return (
      <div className="tour-editor">
        {erro ? <div className="feedback-banner feedback-banner--error">{erro}</div> : null}
        <div className="cameras-form">
          <label className="portal-field cameras-form__largo">
            <span>Nome do tour</span>
            <input
              type="text"
              maxLength={100}
              value={form.nome}
              placeholder="Ex.: Caixas de todas as lojas"
              onChange={(e) => setCampo('nome', e.target.value)}
              autoFocus
            />
          </label>
          <label className="portal-field">
            <span>Layout</span>
            <select value={form.layout} onChange={(e) => setCampo('layout', e.target.value)}>
              {LAYOUTS.map((l) => <option key={l.id} value={l.id}>{l.rotulo}</option>)}
            </select>
          </label>
          <label className="portal-field">
            <span>Tempo em cada tela</span>
            <select value={form.intervalo} onChange={(e) => setCampo('intervalo', Number(e.target.value))}>
              {INTERVALOS.map((s) => <option key={s} value={s}>{s} segundos</option>)}
            </select>
          </label>
          <label className="portal-field">
            <span>Ordem na lista</span>
            <input type="number" value={form.ordem} onChange={(e) => setCampo('ordem', e.target.value)} />
          </label>
          <label className="portal-field cameras-form__check">
            <input type="checkbox" checked={!!form.ativo} onChange={(e) => setCampo('ativo', e.target.checked)} />
            <span>Tour ativo</span>
          </label>
        </div>

        <div className="tour-editor__listas">
          <section>
            <h4>Câmeras disponíveis</h4>
            <div className="tour-editor__filtros">
              <input
                type="search"
                placeholder="Buscar"
                value={filtro.busca}
                onChange={(e) => setFiltro((f) => ({ ...f, busca: e.target.value }))}
              />
              <select value={filtro.equip} onChange={(e) => setFiltro((f) => ({ ...f, equip: e.target.value }))}>
                <option value="">Todos os equipamentos</option>
                {gravadores.map((g) => <option key={g.id} value={g.id}>{g.nome}</option>)}
              </select>
              <select value={filtro.grupo} onChange={(e) => setFiltro((f) => ({ ...f, grupo: e.target.value }))}>
                <option value="">Todos os grupos</option>
                {grupos.map((g) => <option key={g.id} value={g.id}>{g.nome}</option>)}
              </select>
            </div>
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              disabled={!disponiveis.length}
              onClick={() => adicionar(disponiveis.map((c) => c.id))}
            >
              <i className="bi bi-plus-lg" /> Adicionar todas as filtradas ({disponiveis.length})
            </button>
            <ul className="tour-editor__lista">
              {disponiveis.map((c) => (
                <li key={c.id}>
                  <span>{rotulo(c)}{c.ativo ? '' : ' (oculta)'}</span>
                  <button type="button" className="btn btn-secondary btn-sm" onClick={() => adicionar([c.id])} title="Adicionar ao tour">
                    <i className="bi bi-plus-lg" />
                  </button>
                </li>
              ))}
            </ul>
          </section>

          <section>
            <h4>No tour ({form.camera_ids.length}) — nesta ordem</h4>
            {form.camera_ids.length === 0 && <p className="cell-muted">Adicione câmeras da lista ao lado.</p>}
            <ol className="tour-editor__lista">
              {form.camera_ids.map((id, i) => {
                const c = camerasPorId.get(id);
                return (
                  <li key={id}>
                    <span>{c ? rotulo(c) : `Câmera ${id}`}</span>
                    <span style={{ display: 'flex', gap: 4 }}>
                      <button type="button" className="btn btn-secondary btn-sm" onClick={() => mover(i, -1)} disabled={i === 0} title="Subir">
                        <i className="bi bi-arrow-up" />
                      </button>
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        onClick={() => mover(i, 1)}
                        disabled={i === form.camera_ids.length - 1}
                        title="Descer"
                      >
                        <i className="bi bi-arrow-down" />
                      </button>
                      <button type="button" className="btn btn-outline-danger btn-sm" onClick={() => remover(id)} title="Tirar do tour">
                        <i className="bi bi-x-lg" />
                      </button>
                    </span>
                  </li>
                );
              })}
            </ol>
          </section>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => setForm(null)} disabled={salvando}>
            Voltar
          </button>
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={salvar}
            disabled={salvando || !form.nome.trim() || !form.camera_ids.length}
          >
            <i className="bi bi-save me-1" />
            {salvando ? 'Salvando…' : 'Salvar tour'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      {erro ? <div className="feedback-banner feedback-banner--error" style={{ marginBottom: 12 }}>{erro}</div> : null}
      <div className="cameras-config__topo">
        <small className="cell-muted">
          Cada tour aparece em “Ver” na tela de câmeras. Para abrir uma TV direto num tour, use o endereço com ?ver=tour:N.
        </small>
        <button type="button" className="btn btn-primary btn-sm" onClick={() => setForm({ ...FORM_VAZIO })}>
          <i className="bi bi-plus-lg me-1" /> Novo tour
        </button>
      </div>
      {tours.length === 0 ? (
        <div className="empty-state">
          <strong>Nenhum tour personalizado</strong>
          <span>Crie, por exemplo, um tour só com as câmeras dos caixas.</span>
        </div>
      ) : (
        <div className="table-shell">
          <table className="portal-table">
            <thead>
              <tr>
                <th>Tour</th>
                <th>Câmeras</th>
                <th>Layout</th>
                <th>Troca</th>
                <th>Endereço para TV</th>
                <th style={{ width: 110 }} />
              </tr>
            </thead>
            <tbody>
              {tours.map((t) => (
                <tr key={t.id} style={t.ativo ? undefined : { opacity: 0.55 }}>
                  <td className="cell-strong">{t.nome}{t.ativo ? '' : ' (inativo)'}</td>
                  <td>{t.camera_ids.length}</td>
                  <td>{LAYOUTS.find((l) => l.id === t.layout)?.rotulo || t.layout}</td>
                  <td>{t.intervalo}s</td>
                  <td><code>/ti/cameras?ver=tour:{t.id}</code></td>
                  <td>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button type="button" className="btn btn-secondary btn-sm" onClick={() => setForm({ ...FORM_VAZIO, ...t })} title="Editar">
                        <i className="bi bi-pencil" />
                      </button>
                      <button type="button" className="btn btn-outline-danger btn-sm" onClick={() => excluir(t)} title="Excluir">
                        <i className="bi bi-trash" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
